/**
 * Settings — a cog in the toolbar row that opens a small panel under it
 * (Ilias, 04/11/2026). It holds one thing for now, Quality:
 *
 *   Auto     the adaptive controller follows the frame rate (the default)
 *   High     pinned to the top rung, as the editor shows it
 *   Medium   pinned to the middle
 *   Low      pinned to the phone rung, for an old phone or laptop
 *
 * The pick is remembered per browser (savedQuality / localStorage), so a
 * visitor who chose Low on an old phone gets Low next time without asking.
 * mount.ts applies it right after building the controller, before the
 * readiness gate, so the first frame already loads at that quality.
 *
 * QualityPicker is the segmented control on its own: the stand demo's phone
 * menu puts the same one on its Q row (ui/demo-menu.ts).
 */

import type { AdaptiveQualityController, QualityMode } from '../quality/adaptive-quality';
import { resolveLang, type UiLang } from './lang';

export const ICON_COG =
    '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">' +
    '<circle cx="12" cy="12" r="3.2"/>' +
    '<path d="M19.4 15a1.7 1.7 0 0 0 .34 1.87l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.7 1.7 0 0 0-1.87-.34 1.7 1.7 0 0 0-1 1.55V21a2 2 0 1 1-4 0v-.09a1.7 1.7 0 0 0-1.1-1.55 1.7 1.7 0 0 0-1.87.34l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.7 1.7 0 0 0 4.6 15a1.7 1.7 0 0 0-1.55-1H3a2 2 0 1 1 0-4h.09A1.7 1.7 0 0 0 4.6 8.9a1.7 1.7 0 0 0-.34-1.87l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.7 1.7 0 0 0 9 4.6h.1A1.7 1.7 0 0 0 10 3.05V3a2 2 0 1 1 4 0v.09a1.7 1.7 0 0 0 1 1.55 1.7 1.7 0 0 0 1.87-.34l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.7 1.7 0 0 0 19.4 9v.1a1.7 1.7 0 0 0 1.55 1H21a2 2 0 1 1 0 4h-.09a1.7 1.7 0 0 0-1.51 1z"/>' +
    '</svg>';

/** Sliders: the Quality row in the viewer's menu, whose own button is the cog. */
const ICON_QUALITY =
    '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round">' +
    '<path d="M4 7h9M19 7h1M4 17h3M13 17h7"/><circle cx="16" cy="7" r="2.4"/><circle cx="10" cy="17" r="2.4"/>' +
    '</svg>';

const QUALITY_KEY = 'ollo.quality';

/** The modes a visitor can pick ('off' is a dev freeze, not offered). */
const PICKABLE = ['auto', 'high', 'medium', 'low'] as const;
type Pickable = typeof PICKABLE[number];

type Copy = {
    settings: string;
    quality: string;
    modes: Record<Pickable, string>;
    notes: Record<Pickable, string>;
};

const COPY: Record<UiLang, Copy> = {
    en: {
        settings: 'Settings',
        quality: 'Quality',
        modes: { auto: 'Auto', high: 'High', medium: 'Medium', low: 'Low' },
        notes: {
            auto: 'Adjusts itself to keep the view smooth.',
            high: 'The sharpest picture. Needs a powerful device.',
            medium: 'A balance between sharp and smooth.',
            low: 'The smoothest on older phones and laptops.'
        }
    },
    es: {
        settings: 'Ajustes',
        quality: 'Calidad',
        modes: { auto: 'Auto', high: 'Alta', medium: 'Media', low: 'Baja' },
        notes: {
            auto: 'Se ajusta sola para que todo vaya fluido.',
            high: 'La imagen más nítida. Necesita un dispositivo potente.',
            medium: 'Un equilibrio entre nitidez y fluidez.',
            low: 'Lo más fluido en móviles y portátiles antiguos.'
        }
    },
    ca: {
        settings: 'Ajustos',
        quality: 'Qualitat',
        modes: { auto: 'Auto', high: 'Alta', medium: 'Mitjana', low: 'Baixa' },
        notes: {
            auto: 'S’ajusta sola perquè tot vagi fluid.',
            high: 'La imatge més nítida. Cal un dispositiu potent.',
            medium: 'Un equilibri entre nitidesa i fluïdesa.',
            low: 'El més fluid en mòbils i portàtils antics.'
        }
    }
};

function isPickable(m: string | null): m is Pickable {
    return (PICKABLE as readonly string[]).includes(m ?? '');
}

/** The quality this browser picked last time, or null (never picked, or
 *  storage blocked — a private window, an embed without storage access). */
export function savedQuality(): QualityMode | null {
    try {
        const v = localStorage.getItem(QUALITY_KEY);
        return isPickable(v) ? v : null;
    } catch {
        return null;
    }
}

function saveQuality(m: Pickable): void {
    try { localStorage.setItem(QUALITY_KEY, m); } catch { /* not remembered, still applied */ }
}

/** Auto / High / Medium / Low as one row of segments, plus a line saying
 *  what the one in use does. */
export class QualityPicker {

    readonly element: HTMLDivElement;

    private buttons = new Map<Pickable, HTMLButtonElement>();

    private note: HTMLParagraphElement;

    private t = COPY[resolveLang()];

    constructor(private controller: AdaptiveQualityController) {
        this.element = document.createElement('div');
        this.element.className = 'ollo-quality';

        const seg = document.createElement('div');
        seg.className = 'ollo-seg';
        seg.setAttribute('role', 'radiogroup');
        seg.setAttribute('aria-label', this.t.quality);
        for (const m of PICKABLE) {
            const b = document.createElement('button');
            b.type = 'button';
            b.className = 'ollo-seg-opt';
            b.setAttribute('role', 'radio');
            b.textContent = this.t.modes[m];
            b.addEventListener('click', () => this.pick(m));
            this.buttons.set(m, b);
            seg.appendChild(b);
        }

        this.note = document.createElement('p');
        this.note.className = 'ollo-quality-note';

        this.element.append(seg, this.note);
        this.sync();
    }

    /** Re-read the controller: the camera tour holds High while it plays and
     *  hands the mode back afterwards, behind the picker's back. */
    sync(): void {
        const mode = this.controller.currentMode;
        for (const [m, b] of this.buttons) {
            b.classList.toggle('on', m === mode);
            b.setAttribute('aria-checked', m === mode ? 'true' : 'false');
        }
        this.note.textContent = isPickable(mode) ? this.t.notes[mode] : '';
    }

    private pick(m: Pickable): void {
        this.controller.setMode(m);
        saveQuality(m);
        this.sync();
    }
}

export interface SettingsPanelOptions {
    /** The toolbar row the cog joins, and the button it goes in front of
     *  (null = at the end of the row). */
    toolbar: HTMLElement;
    before?: Element | null;
    /** Where the panel lives (the ui root). */
    parent: HTMLElement;
    quality: AdaptiveQualityController;
    /** A row in the viewer's cog menu: named Quality, with the sliders icon,
     *  so the menu doesn't show a cog inside a cog. */
    asQualityRow?: boolean;
}

export class SettingsPanel {

    readonly cog: HTMLButtonElement;

    private panel: HTMLDivElement;

    private picker: QualityPicker;

    private opened = false;

    private onDocDown = (e: PointerEvent) => {
        // The cog's own press is the toggle's job; closing here first would
        // let its click open the panel straight back up.
        if (!this.opened || this.cog.contains(e.target as Node)) return;
        this.close();
    };

    private onKey = (e: KeyboardEvent) => {
        if (e.key === 'Escape' && this.opened) this.close();
    };

    constructor(opts: SettingsPanelOptions) {
        const t = COPY[resolveLang()];

        this.cog = document.createElement('button');
        this.cog.type = 'button';
        this.cog.className = 'ollo-btn ollo-toolbar-btn ollo-settings-cog';
        const name = opts.asQualityRow ? t.quality : t.settings;
        this.cog.innerHTML = (opts.asQualityRow ? ICON_QUALITY : ICON_COG) + `<span class="ollo-toolbar-label">${name}</span>`;
        this.cog.setAttribute('aria-label', name);
        this.cog.setAttribute('aria-haspopup', 'dialog');
        this.cog.setAttribute('aria-expanded', 'false');
        this.cog.addEventListener('click', () => this.toggle());
        opts.toolbar.insertBefore(this.cog, opts.before ?? null);

        this.panel = document.createElement('div');
        this.panel.className = 'ollo-settings';
        this.panel.setAttribute('role', 'dialog');
        this.panel.setAttribute('aria-label', t.settings);
        // Taps inside stay inside: the document listener closes on anything else.
        this.panel.addEventListener('pointerdown', (e) => e.stopPropagation());

        const head = document.createElement('p');
        head.className = 'ollo-settings-head';
        head.textContent = t.quality;
        this.picker = new QualityPicker(opts.quality);
        this.panel.append(head, this.picker.element);
        opts.parent.appendChild(this.panel);

        document.addEventListener('pointerdown', this.onDocDown);
        window.addEventListener('keydown', this.onKey);
    }

    open(): void {
        if (this.opened) return;
        this.opened = true;
        this.picker.sync();
        this.panel.classList.add('open');
        this.cog.classList.add('active');
        this.cog.setAttribute('aria-expanded', 'true');
    }

    close(): void {
        if (!this.opened) return;
        this.opened = false;
        this.panel.classList.remove('open');
        this.cog.classList.remove('active');
        this.cog.setAttribute('aria-expanded', 'false');
    }

    toggle(): void {
        if (this.opened) this.close();
        else this.open();
    }

    dispose(): void {
        document.removeEventListener('pointerdown', this.onDocDown);
        window.removeEventListener('keydown', this.onKey);
        this.cog.remove();
        this.panel.remove();
    }
}
